import React, { useState, useEffect } from 'react'
import Sidebar from '../../mini-comp/Sidebar'
import Topbar from '../../mini-comp/Topbar'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useParams } from 'react-router-dom'
import { RiPencilLine } from 'react-icons/ri'

const ProfessionalDetails = () => {
    const baseUrl = "https://api.recruit.burrayhr.com";
    const [jobSeeker, setJobSeeker] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [fetchError, setFetchError] = useState('')
    const [editModal, setEditModal] = useState({ open: false, type: '', idx: -1, value: '', file: null, error: '', loading: false, id: undefined });
    const { id } = useParams()

    useEffect(() => {
        let isCancelled = false
        const fetchJobSeeker = async () => {
            setIsLoading(true)
            setFetchError('')
            try {
                const response = await axios.get(`${baseUrl}/api/job-seekers/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${Cookies.get('token')}`
                    }
                })
                if (!isCancelled) setJobSeeker(response.data)
                console.log(response.data)
            } catch (err) {
                if (!isCancelled) setFetchError(err?.response?.data?.detail || err?.message || 'Failed to load job seeker')
            } finally {
                if (!isCancelled) setIsLoading(false)
            }
        }
        fetchJobSeeker()
        return () => { isCancelled = true }
    }, [id])

    return (
        <div>
            <Topbar />
            <Sidebar />

            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                {/* Loading State */}
                                {isLoading && (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mt-2">Loading professional details...</p>
                                    </div>
                                )}

                                {/* Error State */}
                                {!!fetchError && (
                                    <div className="alert alert-danger" role="alert">
                                        {fetchError}
                                    </div>
                                )}

                                {/* Professional Overview */}
                                {jobSeeker && (
                                    <>
                                        <div className="card mb-4">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center gap-4">
                                                    {(() => {
                                                        const u = jobSeeker || {}
                                                        const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || 'Unnamed'
                                                        const profession = u.profession || '—'
                                                        const city = u.city || u.address || '—'
                                                        const expYears = typeof u.experience === 'string' ? u.experience : (u.experience ?? '0')
                                                        const rawImg = u.profile_picture
                                                        const imgUrl = rawImg ? (String(rawImg).startsWith('http') ? rawImg : `${baseUrl}${rawImg}`) : null
                                                        const initials = fullName.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?'
                                                        const autoApply = !!u.auto_apply

                                                        return (
                                                            <>
                                                                <div className="flex-shrink-0">
                                                                    <div className="avatar avatar-xxl">
                                                                        {imgUrl ? (
                                                                            <img src={imgUrl} alt={fullName} className="rounded-circle" />
                                                                        ) : (
                                                                            <div className="avatar-title bg-primary-subtle text-primary rounded-circle fw-bold fs-3">
                                                                                {initials}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex-grow-1">
                                                                    <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                                                                        <h4 className="mb-0">{fullName}</h4>
                                                                        <span className="badge bg-label-primary">{profession}</span>
                                                                        <span className="badge bg-label-info">{city}</span>
                                                                        <span className="badge bg-label-success">{expYears} yrs exp</span>
                                                                        {autoApply && (
                                                                            <span className="badge bg-label-warning">Auto-apply: On</span>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-muted mb-3">{u.bio || 'No bio provided.'}</p>
                                                                    <div className="d-flex flex-wrap gap-2">
                                                                        {u.phone && <span className="badge bg-light text-dark border">{u.phone}</span>}
                                                                        {u.gender && <span className="badge bg-light text-dark border">Gender: {u.gender}</span>}
                                                                        {u.marital_status && <span className="badge bg-light text-dark border">Marital: {u.marital_status}</span>}
                                                                        {u.birth_date && <span className="badge bg-light text-dark border">DOB: {u.birth_date}</span>}
                                                                        {u.job_category && <span className="badge bg-light text-dark border">Category: {u.job_category}</span>}
                                                                        {u.employment_status && (
                                                                            <span className="badge bg-warning-subtle text-warning">
                                                                                {u.employment_status.charAt(0).toUpperCase() + u.employment_status.slice(1)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Skills, Qualifications, and CVs Grid */}
                                        <div className="row g-4">
                                            {/* Skills Card */}
                                            <div className="col-md-4">
                                                <div className="card h-100">
                                                    <div className="card-header">
                                                        <h5 className="card-title mb-0">Skills</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        {(() => {
                                                            const items = Array.isArray(jobSeeker?.skills_detail) ? jobSeeker.skills_detail : []
                                                            return items.length ? (
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {items.map((s, idx) => {
                                                                        const label = typeof s === 'string' ? s : (s?.name || s?.skill || 'Skill');
                                                                        return (
                                                                            <span key={idx} className="badge bg-info-subtle text-info d-inline-flex align-items-center gap-2">
                                                                                {label}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setEditModal({
                                                                                        open: true,
                                                                                        type: 'skill',
                                                                                        idx,
                                                                                        id: s?.id,
                                                                                        value: s?.name || s,
                                                                                        error: '',
                                                                                        loading: false,
                                                                                        file: null
                                                                                    })}
                                                                                    className="btn-icon btn-sm p-0 border-0 bg-transparent text-info"
                                                                                    aria-label="Edit Skill"
                                                                                >
                                                                                    <RiPencilLine size={14} />
                                                                                </button>
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-muted">No skills provided.</div>
                                                            )
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Qualifications Card */}
                                            <div className="col-md-4">
                                                <div className="card h-100">
                                                    <div className="card-header">
                                                        <h5 className="card-title mb-0">Qualifications</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        {(() => {
                                                            const quals = Array.isArray(jobSeeker?.qualifications_detail) ? jobSeeker.qualifications_detail : []
                                                            return quals.length ? (
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {quals.map((q, idx) => {
                                                                        const label = typeof q === 'string' ? q : (q?.title || q?.name || 'Qualification');
                                                                        return (
                                                                            <span key={idx} className="badge bg-warning-subtle text-warning d-inline-flex align-items-center gap-2">
                                                                                {label}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setEditModal({
                                                                                        open: true,
                                                                                        type: 'qualification',
                                                                                        idx,
                                                                                        id: q?.id,
                                                                                        value: q?.title || q,
                                                                                        error: '',
                                                                                        loading: false,
                                                                                        file: null
                                                                                    })}
                                                                                    className="btn-icon btn-sm p-0 border-0 bg-transparent text-warning"
                                                                                    aria-label="Edit Qualification"
                                                                                >
                                                                                    <RiPencilLine size={14} />
                                                                                </button>
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-muted">No qualifications provided.</div>
                                                            )
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CVs Card */}
                                            <div className="col-md-4">
                                                <div className="card h-100">
                                                    <div className="card-header">
                                                        <h5 className="card-title mb-0">CVs</h5>
                                                    </div>
                                                    <div className="card-body">
                                                        {(() => {
                                                            const cvs = Array.isArray(jobSeeker?.cvs_detail) ? jobSeeker.cvs_detail : [];
                                                            return cvs.length ? (
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {cvs.map((cv, idx) => {
                                                                        const label = cv?.name || cv?.title || `CV ${idx + 1}`;
                                                                        const fileUrl = cv?.file ? (String(cv.file).startsWith('http') ? cv.file : `${baseUrl}${cv.file}`) : '';
                                                                        return (
                                                                            <span key={idx} className="badge bg-primary-subtle text-primary d-inline-flex align-items-center gap-2">
                                                                                {fileUrl ? (
                                                                                    <a href={fileUrl} target="_blank" rel="noreferrer" className="text-decoration-none text-primary">
                                                                                        {label}
                                                                                    </a>
                                                                                ) : label}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => setEditModal({
                                                                                        open: true,
                                                                                        type: 'cv',
                                                                                        idx,
                                                                                        id: cv?.id,
                                                                                        value: label,
                                                                                        error: '',
                                                                                        loading: false,
                                                                                        file: null
                                                                                    })}
                                                                                    className="btn-icon btn-sm p-0 border-0 bg-transparent text-primary"
                                                                                    aria-label="Edit CV"
                                                                                >
                                                                                    <RiPencilLine size={14} />
                                                                                </button>
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : (
                                                                <div className="text-muted">No CVs uploaded.</div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProfessionalDetails
